import { ListContainer, ExecuteParams, ExecutionContext, OutlineTree, PromptFunction, TextContainer } from '../../types';
import { createCustomFunction } from './createFunction';
import { getTreeDataById, NovelSceneUtils, parseSceneToXML } from '../../utils';
import { CoreEntityType, NovelActTree, NovelSceneSchema } from '#/index';
import { getPreviousTreeDataById, getNextTreeDataById } from '@/utils/novel';
import { structuralUpdate, convertJSONContentToFormattedText } from '@/utils';

const getSceneByElements = (context: ExecutionContext,
  elements: ExecuteParams[],
  scenes: NovelSceneSchema[]) => {
  const utils = new NovelSceneUtils(context, 'intersection').initResult(scenes);
  elements.forEach(element => {
    if (typeof element === 'object' && element.type === CoreEntityType.Novels) {
      utils.byNovel(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelActs) {
      utils.byAct(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelChapters) {
      utils.byChapter(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelScenes) {
      utils.byScene(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.CodexEntries) {
      utils.byCodexEntry(element)
    } else if (typeof element === 'object' && element.type === 'codexDetail') {
      utils.byCodexDetail(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.CodexCategories) {
      utils.byCodexCategory(element)
    } else if (typeof element === 'object' && element.type === 'novelLabel') {
      utils.byLabel(element)
    } else if (typeof element === 'object' && element.type === 'pointOfView') {
      utils.byPov(element)
    } else if (typeof element === 'object' && element.type === 'textContainer' && element.reference) {
      const utilsResult = getSceneByElements(context, [element.reference], scenes)
      utilsResult.getResult().forEach(scene => {
        utils.byScene(scene)
      })
    } else if (typeof element === 'object' && element.type === 'listContainer') {
      const utilsResult = getSceneByElements(context, element.elements, scenes)
      utilsResult.getResult().forEach(scene => {
        utils.byScene(scene)
      })
    }
  })
  return utils;
}

const getOutlineByElements = (context: ExecutionContext,
  params: ExecuteParams[],
  trees: NovelActTree[],
  mode: 'content' | 'summary',
  rootName?: string
): OutlineTree | undefined => {
  const scenes: NovelSceneSchema[] = [];
  trees.forEach(tree => {
    tree.children?.forEach(chapter => {
      chapter.children?.forEach(scene => {
        if (scene.data.type === CoreEntityType.NovelScenes) {
          scenes.push(scene.data)
        }
      })
    })
  })
  const utils = getSceneByElements(context, params, scenes);
  const result = utils.getOutline();
  if (result.length) {
    return {
      type: 'outlineTree',
      data: result,
      mode: mode,
      rootName: rootName,
    }
  }
}


/**
 * 上下文相关函数
 * @returns 上下文相关函数对象
 */
const contextFunctions: Record<string, PromptFunction> = {
  hasTextAfter: createCustomFunction(
    'hasTextAfter',
    '检查所选文本后是否有文本',
    (context) => 'Boolean(context.globalContext.textAfter && context.globalContext.textAfter.trim())'
  ),

  hasTextBefore: createCustomFunction(
    'hasTextBefore',
    '检查所选文本前是否有文本',
    (context) => 'Boolean(context.globalContext.textBefore && context.globalContext.textBefore.trim())'
  ),
  message: createCustomFunction(
    'message',
    '获取当前续写块/聊天框/文本替换选择的消息内容',
    (context): TextContainer => {
      const content = context.globalContext.currentPosition?.message;
      const textContent = content ? convertJSONContentToFormattedText(content) : '';
      return {
        type: 'textContainer',
        title: textContent,
        defaultFormat: 'plain',
        formatters: {
          plain: (container) => container.title,
        },
      }
    }
  ),
  storySoFar: createCustomFunction(
    'storySoFar',
    '（根据给定参数）返回截至当前位置的小说情节概要。',
    (context, ...params: ExecuteParams[]) => {
      const { generateStore, currentPosition } = context.globalContext
      const previousTree = currentPosition?.scene?.id ? getPreviousTreeDataById(generateStore.getState().novelActsTree, currentPosition?.scene?.id) : undefined
      return previousTree ? getOutlineByElements(context, params, previousTree, 'content', 'storySoFar') : undefined
    }
  ),
  storyToCome: createCustomFunction(
    'storyToCome',
    '（根据给定参数）返回从当前位置到小说结尾的情节概要。',
    (context, ...params: ExecuteParams[]) => {
      const { generateStore, currentPosition } = context.globalContext
      const nextTree = currentPosition?.scene?.id ? getNextTreeDataById(generateStore.getState().novelActsTree, currentPosition?.scene?.id) : undefined
      return nextTree ? getOutlineByElements(context, params, nextTree, 'content', 'storyToCome') : undefined
    }
  ),
  textAfter: createCustomFunction(
    'textAfter',
    '获取文档/场景中紧接当前位置之后的文本内容。',
    (context) => {
      if (context.globalContext.currentPosition?.sceneToCome) {
        return {
          type: 'textContainer',
          title: convertJSONContentToFormattedText(context.globalContext.currentPosition.sceneToCome),
          defaultFormat: 'plain',
          formatters: {
            plain: (container) => container.title,
          },
        }
      }
    },
  ),
  textBefore: createCustomFunction(
    'textBefore',
    '获取文档/场景中紧接当前位置之前的文本内容。',
    (context) => {
      if (context.globalContext.currentPosition?.sceneSoFar) {
        return {
          type: 'textContainer',
          title: convertJSONContentToFormattedText(context.globalContext.currentPosition.sceneSoFar),
          defaultFormat: 'plain',
          formatters: {
            plain: (container) => container.title,
          },
        }
      }
    }
  ),
  textSoFar: createCustomFunction(
    'textSoFar',
    '（根据给定参数）返回截至当前位置的小说文本内容。',
    (context, ...params: ExecuteParams[]): ListContainer<OutlineTree | TextContainer> => {
      const { generateStore, currentPosition } = context.globalContext
      const previousTree = currentPosition?.scene?.id ? getPreviousTreeDataById(generateStore.getState().novelActsTree, currentPosition?.scene?.id) : undefined
      const result: (OutlineTree | TextContainer)[] = []
      if (previousTree) {
        const outline = getOutlineByElements(context, params, previousTree, 'content', 'storySoFar')
        if (outline) {
          result.push(outline)
        }
      }
      if (currentPosition?.scene && currentPosition?.sceneSoFar) {
        let sceneData = getTreeDataById(context, currentPosition.scene.id);

        if (sceneData) {
          // 设置场景内容
          sceneData = structuralUpdate(sceneData, {
            data: {
              attributes: {
                content: currentPosition?.sceneSoFar,
              },
            }
          })
          result.push({
            type: 'textContainer',
            title: parseSceneToXML(sceneData, context),
            defaultFormat: 'plain',
            formatters: {
              plain: (container) => container.title,
            },
          })
        }
      }
      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    }
  ),
  textToCome: createCustomFunction(
    'textToCome',
    '（根据给定参数）返回从当前位置到小说结尾的文本内容。',
    (context, ...params: ExecuteParams[]): ListContainer<OutlineTree | TextContainer> => {
      const { generateStore, currentPosition } = context.globalContext
      const nextTree = currentPosition?.scene?.id ? getNextTreeDataById(generateStore.getState().novelActsTree, currentPosition?.scene?.id) : undefined
      const result: (OutlineTree | TextContainer)[] = []
      if (nextTree) {
        const outline = getOutlineByElements(context, params, nextTree, 'content', 'storyToCome')
        if (outline) {
          result.push(outline)
        }
      }
      if (currentPosition?.scene && currentPosition?.sceneToCome) {
        let sceneData = getTreeDataById(context, currentPosition.scene.id);

        if (sceneData) {
          // 设置场景内容
          sceneData = structuralUpdate(sceneData, {
            data: {
              attributes: {
                content: currentPosition?.sceneToCome,
              },
            }
          })
          result.push({
            type: 'textContainer',
            title: parseSceneToXML(sceneData, context),
            defaultFormat: 'plain',
            formatters: {
              plain: (container) => container.title,
            },
          })
        }
      }
      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    }
  ),
  isEndOfText: createCustomFunction(
    'isEndOfText',
    '检查当前位置是否位于文档/场景的末尾（即之后没有文本）。',
    (context): boolean => {
      const { currentPosition } = context.globalContext
      return !currentPosition?.sceneToCome
    }
  ),
  isStartOfText: createCustomFunction(
    'isStartOfText',
    '检查当前位置是否位于文档/场景的开头（即之前没有文本）。',
    (context): boolean => {
      const { currentPosition } = context.globalContext
      return !currentPosition?.sceneSoFar
    }
  ),
}

export default contextFunctions;